import { Center, Spinner } from '@dapperlabs/core-fe';
import React from 'react';
import { ErrorPage } from 'src/general/components/ErrorPage/ErrorPage';
import { FS } from 'src/general/constants/finiteStates';
import { useTranslation } from 'src/lib/translations';
import { Box, Button, Flex, Grid, GridItem, Text } from 'src/theme';

import { MyPackListItem } from './MyPackListItem';
import { usePackList } from './usePackList';

// If pack status is sealed then it is not open
const isPackOpen = (status: string) => {
  if (status.toLowerCase() === 'sealed') return false;
  return true;
};

export interface MyPacksListProps {
  flowAddress: string;
  isOwnProfile?: boolean;
  username: string;
}

export const MyPacksList = ({
  isOwnProfile,
  flowAddress,
  username,
}: MyPacksListProps) => {
  const { t } = useTranslation();

  const { page, advancePage, queryState, packs, hasNextPage } =
    usePackList(flowAddress);

  if ((queryState === FS.IDLE || queryState === FS.LOADING) && page === 1) {
    return (
      <Center minHeight={{ base: '40vh', md: '60vh' }}>
        <Spinner />
      </Center>
    );
  }

  if (queryState === FS.ERROR) {
    const errorTitle = t('MyMoments.error');
    return (
      <Center minHeight={{ base: '40vh', md: '60vh' }}>
        <ErrorPage title={errorTitle} />
      </Center>
    );
  }

  return (
    <Box my='1.5rem'>
      {packs?.length > 0 ? (
        <>
          <Grid
            gap={4}
            templateColumns={{
              lg: 'repeat(4, 1fr)',
              md: 'repeat(3, 1fr)', // intermediate before switching to 1 per row
            }}
          >
            {packs?.map((pack) => (
              <GridItem key={pack.id}>
                <MyPackListItem
                  distributionId={pack.dist_id}
                  flowId={pack.id}
                  isOpen={isPackOpen(pack.status)}
                  isOwnProfile={isOwnProfile}
                  packImageUrl={pack.distributionImageUrl}
                  packName={pack.title}
                  tier={pack.tier}
                  totalMoments={pack.numberOfPackSlots}
                  username={username}
                />
              </GridItem>
            ))}
          </Grid>
          {(hasNextPage || queryState === FS.LOADING) && (
            <Flex justifyContent='center' mt={8} mb={8}>
              <Button
                isLoading={queryState === FS.LOADING}
                onClick={advancePage}
              >
                {t('MarketplaceSearch.FetchMore')}
              </Button>
            </Flex>
          )}
        </>
      ) : (
        <Box alignItems='center' justifyContent='center'>
          <Text color='gray.50' fontSize='1rem'>
            {isOwnProfile
              ? t('MyMoments.Packs.EmptyCollectionSelf')
              : t('MyMoments.Packs.EmptyCollection', { username })}
          </Text>
        </Box>
      )}
    </Box>
  );
};
